<template>
    <UiModalMobile
        :is-open="isOpen"
        title="Display Options"
        @close="handleCancel"
    >
        <template #content>
            <div class="space-y-6">
                <!-- Group By Section -->
                <div>
                    <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                        Group by
                    </h3>
                    <div class="space-y-3">
                        <label
                            v-for="option in groupByOptions"
                            :key="option.value"
                            class="flex items-center gap-3 cursor-pointer"
                        >
                            <input
                                type="radio"
                                :value="option.value"
                                :checked="viewMode === option.value"
                                @change="selectGroupBy(option.value)"
                                class="text-10x-orange form-radio"
                                name="groupBy"
                            />
                            <span class="text-slate-700 dark:text-slate-300 text-sm">
                                {{ option.label }}
                            </span>
                        </label>
                    </div>
                </div>

                <hr class="border-slate-200 dark:border-slate-700" />

                <!-- Show Card Numbers Section -->
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                                Show card #s
                            </h3>
                        </div>
                        <Switch
                            :value="showLastFour"
                            @input="toggleShowLastFour"
                        />
                    </div>
                </div>

                <hr class="border-slate-200 dark:border-slate-700" />

                <!-- Show Inactive Benefits Section -->
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                                Show inactive benefits
                            </h3>
                        </div>
                        <Switch
                            :value="showInactiveBenefits"
                            @input="toggleShowInactiveBenefits"
                        />
                    </div>
                </div>

                <hr class="border-slate-200 dark:border-slate-700" />

                <!-- Show Hidden Benefits Section -->
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                                Show hidden benefits
                            </h3>
                        </div>
                        <Switch
                            :value="showHiddenBenefits"
                            @input="toggleShowHiddenBenefits"
                        />
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex">
                <button
                    class="btn-base btn-dark flex-1"
                    @click="handleApply"
                >
                    Apply
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Switch from '@/components/ui/switch/switch.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const store = useCardBenefitsStore();
const { showLastFour, showInactiveBenefits, showHiddenBenefits, viewMode } =
    storeToRefs(store);

// Store original values when modal opens
const originalValues = ref({
    showLastFour: false,
    showInactiveBenefits: false,
    showHiddenBenefits: false,
    viewMode: 'flat',
});

// Watch for modal opening to capture original values
watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen) {
            originalValues.value = {
                showLastFour: showLastFour.value,
                showInactiveBenefits: showInactiveBenefits.value,
                showHiddenBenefits: showHiddenBenefits.value,
                viewMode: viewMode.value,
            };
        }
    }
);

const groupByOptions = [
    { value: 'flat', label: 'None' },
    { value: 'by_card', label: 'Card' },
];

const toggleShowLastFour = () => {
    store.setShowLastFour(!showLastFour.value);
};

const toggleShowInactiveBenefits = () => {
    store.setShowInactiveBenefits(!showInactiveBenefits.value);
};

const toggleShowHiddenBenefits = () => {
    store.setShowHiddenBenefits(!showHiddenBenefits.value);
};

const selectGroupBy = (value) => {
    // Just set the view mode without fetching data to prevent modal closing
    store.viewMode = value;
};

const handleCancel = () => {
    store.setShowLastFour(originalValues.value.showLastFour);
    store.setShowInactiveBenefits(originalValues.value.showInactiveBenefits);
    if (showHiddenBenefits.value !== originalValues.value.showHiddenBenefits) {
        store.setShowHiddenBenefits(originalValues.value.showHiddenBenefits);
    }
    store.setViewMode(originalValues.value.viewMode);
    emit('close');
};

const handleApply = async () => {
    // Ensure data is fetched for the current view mode before closing
    try {
        await store.setViewMode(viewMode.value);
        emit('close');
    } catch (error) {
        console.error('Error applying view mode:', error);
        emit('close');
    }
};
</script>
